import api from '@/services/api'
import ContentActions from './actions-types'
import store from '@/store'
import BackgroundActions from '@/store/background_actions/actions-types.js'
import BackgroundMutations from '@/store/background_actions/mutations-types.js'
import AppActions from '@/store/app/actions-types.js'
import EntryActions from '@/store/content/entry/actions-types'
import EntryMutations from '@/store/content/entry/mutations-types'
import { BgActionEventBus } from '@/bgActionEventBus'

export default {
  [ContentActions.GET_NODE_ACTIONS]({ }, options = {}) {
    return new Promise((resolve, reject) => {
      api.content.actions.action
        .getNodeActions(options)
        .then((response) => {
          resolve(response.data)
        })
        .catch(() => reject())
    })
  },

  async [ContentActions.UPLOAD_FILE]({ commit, dispatch }, {parentId, file, fieldValues = {}}) {
    let bgActionId = null;
    let nodeId = null;
    let abortUpload = null;
    try {
      // Step 1: Create background action and wait for it
      const bgAction = await dispatch(
        `background_actions/${[BackgroundActions.CREATE_BACKGROUND_ACTION]}`,
        {title: file.name, type: 'upload'},
        { root: true }
      );
      bgActionId = bgAction.bgActionId;

      // Step 2: Prepare file to content and wait for it
      const response = await api.content.actions.upload.prepareFileToContent({parentId, file, fieldValues});

      const fileServiceUrl = response.data.url;
      nodeId = response.data.node_id;
      const fileName = response.data.name;
      const parentExternal = response.data.parent_external_id;
      const callback = response.data.callback;
      const lockToken = new URL(callback).searchParams.get('lock_token');

      // A page/tab close cannot wait for Axios promises after the user accepts
      // the browser's beforeunload prompt. Keep the request details on the
      // background action so App.vue can send this small request with
      // `keepalive` from the pagehide event.
      const contentBaseUrl = store.getters['app/getSelectedContentAppAccessUrl']()
        ?.replace(/\/?job\/?$/, '')
        ?.replace(/\/$/, '')
      bgAction.unloadCleanup = contentBaseUrl ? {
        url: `${contentBaseUrl}/file/abort`,
        token: store.state.core.auth.token,
        body: JSON.stringify({ node_id: nodeId, lock_token: lockToken }),
        // The page is being replaced before the abort response is available.
        // Persist a completed delete action so the new page can refresh the
        // current navigation and remove this prepared upload node.
        refreshMessage: {
          bgActionId,
          type: 'delete',
          status: 'done',
          node_id: nodeId,
          node_parent: parentId,
        }
      } : null

      // This is shared by the explicit cancel button, confirmed route changes
      // and the upload error path. A single request keeps the operation
      // idempotent when cancellation causes Axios to reject the upload.
      let abortRequest = null;
      abortUpload = () => {
        if (!abortRequest) {
          abortRequest = api.content.actions.upload.abortFileUpload({nodeId, lockToken})
            .catch((error) => {
              // 409 means the file-service callback already finished the
              // upload. In that case the node and file are already consistent.
              if (error.response && error.response.status === 409) {
                return
              }

              throw error
            })
        }

        return abortRequest
      }
      bgAction.onCancel = abortUpload

      // The content node now exists, so notify navigation to display its locked
      // thumbnail while the file-service upload continues in the background.
      BgActionEventBus.$emit('bgAction', {
        bgActionId,
        type: 'upload',
        status: 'done',
        node_id: nodeId,
        node_parent: parentId,
      });

      // Step 3: Upload to file service and wait for it
      await api.fileservice.actions.upload.uploadToFileService(
        {nodeId, fileName, file, fileServiceUrl, parentExternal, callback},
        bgAction.cancelSource,
        event => {
          const progress = Math.round((100 * event.loaded) / event.total);
          commit(`background_actions/${[BackgroundMutations.UPDATE_BACKGROUND_ACTION_PROGRESS]}`, {bgActionId, progress}, { root: true });
          commit(`background_actions/${[BackgroundMutations.UPDATE_BACKGROUND_ACTION]}`, {bgActionId, nodeId}, { root: true });
        }
      );

      // A 100% transfer-progress event can arrive before the file-service request
      // has finished processing its response. Only complete the background action
      // once the upload promise has resolved.
      commit(
        `background_actions/${[BackgroundMutations.UPDATE_BACKGROUND_ACTION_STATUS]}`,
        {bgActionId, status: 'done'},
        { root: true }
      );
      bgAction.onCancel = null

      return { bgActionId, nodeId };

    } catch {
      if (abortUpload) {
        try {
          await abortUpload()
        } catch (error) {
          console.error('[upload] unable to abort prepared upload', {nodeId, error})
        }
      }

      if (bgActionId) {
        commit(`background_actions/${[BackgroundMutations.UPDATE_BACKGROUND_ACTION_STATUS]}`, {bgActionId, status: 'error'}, { root: true });
      }
    }
  },

  [ContentActions.DOWNLOAD_FILE]({}, {nodeId, control}) {
      if (!this.downloading) {
        this.downloading = true;
        api.content.actions.file.download(nodeId)
        .then((response) => {
          const link = document.createElement('a')
          link.target = '_blank';
          const downloadLink = new URL(response.data.link)
          link.href = downloadLink.toString()
          link.click()
        })
        .catch((error) => {
          this.downloading = false
          store.dispatch('app/' + AppActions.OPEN_APP_ERROR_MESSAGE, error.message)
        })
        .finally(() => {
          control.downloading = false
          this.downloading = false
        })
      }
  },

  [ContentActions.CUSTOM_ACTION]({}, {nodeId, pipelineId, parameters}) {
    return new Promise((resolve, reject) => {
      api.content.actions.entry
        .customAction(nodeId, pipelineId, parameters)
        .then((response) => {
          resolve(response.data)
        })
        .catch((error) => {
          reject(error)
        })
    })
  },

  [ContentActions.CREATE_FOLDER]({}, params) {
    return new Promise((resolve, reject) => {
      api.content.actions.folder
        .createFolder(params)
        .then((response) => {
          resolve(response.data)
        })
        .catch(() => reject())
    })
  },

  [ContentActions.RENAME]({}, {nodeId, params}) {
    return new Promise((resolve, reject) => {
      api.content.actions.entry
        .renameEntry(nodeId, params)
        .then((response) => {
          resolve(response.data)
        })
        .catch(() => reject())
    })
  },

  [ContentActions.REMOVE]({}, {nodeId, bgGroupId}) {
    return new Promise((resolve, reject) => {
      api.content.actions.entry.removeEntry(nodeId, {bgGroupId: bgGroupId})
      .then((response) => {
        resolve(response.data)
      })
      .catch((error) => {
        reject(error)
      })
    })
  },

  [ContentActions.SYNC]({ }, nodeId) {
    return new Promise((resolve, reject) => {
      api.content.actions.entry
        .syncEntry(nodeId)
        .then((response) => {
          resolve(response.data)
        })
        .catch(() => reject())
    })
  },

  [ContentActions.MOVE]({ commit, dispatch }, {nodeId, params}) {
    return new Promise((resolve, reject) => {
      dispatch(`background_actions/${[BackgroundActions.CREATE_BACKGROUND_ACTION]}`, {title: params.name, icon: 'mdi-folder-move-outline'}, { root:true })
        .then((bgAction) => {
          const bgActionId = bgAction.bgActionId

          api.content.actions.entry.moveEntry(nodeId, params, bgAction.cancelSource, {})
          .then((response) => {
            commit(`background_actions/${[BackgroundMutations.UPDATE_BACKGROUND_ACTION_STATUS]}`, {bgActionId, status: 'done'}, { root:true })

            resolve(response.data)
          })
          .catch((error) => {
            commit(`background_actions/${[BackgroundMutations.UPDATE_BACKGROUND_ACTION_STATUS]}`, {bgActionId, status: 'error'}, { root:true })

            reject(error)
          })
        })
    })
  },

  [ContentActions.MOVE_ENTRIES]({}, params) {
    return api.content.actions.entry.moveEntries(params)
  },

  [ContentActions.COPY]({}, {nodeId, destinationId, data}) {
    return new Promise((resolve, reject) => {
      api.content.actions.entry.copyEntry(nodeId, destinationId, data)
      .then((response) => {
        resolve(response.data)
      })
      .catch((error) => {
        reject(error)
      })
    })
  },

  [ContentActions.COPY_ENTRIES]({}, params) {
    return new Promise((resolve, reject) => {
      api.content.actions.entry.copyEntries(params)
      .then((response) => {
        resolve(response.data)
      })
      .catch((error) => {
        reject(error)
      })
    })
  },

  [ContentActions.REMOVE_ENTRIES]({}, params) {
    return new Promise((resolve, reject) => {
      api.content.actions.entry.removeEntries(params)
      .then((response) => {
        resolve(response.data)
      })
      .catch((error) => {
        reject(error)
      })
    })
  },

  [ContentActions.CREATE_GDOC]({}, params) {
    return new Promise((resolve, reject) => {
      api.content.actions.gdocs
        .createGDoc(params)
        .then((response) => {
          resolve(response.data)
        })
        .catch((error) => reject(error))
    })
  },

  [ContentActions.EDIT_GDOC]({}, nodeId) {
    return new Promise((resolve, reject) => {
      api.content.actions.gdocs
        .editGDoc(nodeId)
        .then((response) => {
          resolve(response.data)
        })
        .catch((error) => reject(error))
    })
  },

  [ContentActions.FINISH_EDIT_GDOC]({}, {nodeId, params}) {
    return new Promise((resolve, reject) => {
      api.content.actions.gdocs
        .finishEditingGDoc(nodeId, params)
        .then((response) => {
          resolve(response.data)
        })
        .catch((error) => reject(error))
    })
  },

  [ContentActions.CONVERT_TO_GDOC]({}, nodeId) {
    return new Promise((resolve, reject) => {
      api.content.actions.gdocs
        .convertToGDoc(nodeId)
        .then((response) => {
          resolve(response.data)
        })
        .catch((error) => reject(error))
    })
  },

  [ContentActions.GENERATE_DOWNLOAD_LINK]({}, {nodeId, params}) {
    return new Promise((resolve, reject) => {
      api.content.actions.action
        .generateDownloadLink(nodeId, params)
        .then((response) => {
          resolve(response.data.download_link)
        })
        .catch(() => reject())
    })
  },

  async [ContentActions.SHARE_MULTI_NODE_DOWNLOAD]({ dispatch, commit }, {parentNodeId, nodeIds, expirationDate: requestedExpirationDate, bgActionId: requestedBgActionId, zipName: requestedZipName}) {
    const randomValue = Math.random().toString(36).slice(2, 12)
    const bgActionId = requestedBgActionId ?? `bg-${Date.now().toString(36)}-${randomValue}`
    const zipName = requestedZipName || `download-${randomValue}.zip`
    const expirationDate = new Date()
    expirationDate.setDate(expirationDate.getDate() + 1)
    const expirationDateString = requestedExpirationDate || `${expirationDate.getFullYear()}-${String(expirationDate.getMonth() + 1).padStart(2, '0')}-${String(expirationDate.getDate()).padStart(2, '0')}`

    await dispatch(
      `background_actions/${[BackgroundActions.CREATE_BACKGROUND_ACTION]}`,
      {
        bgActionId,
        title: zipName,
        type: 'share-download',
        icon: 'mdi-download-multiple',
      },
      { root: true }
    )

    const data = {
      parent_node_id: parentNodeId,
      node_ids: nodeIds,
      expiration_date: expirationDateString,
      bg_action_id: bgActionId,
      zip_name: zipName,
    }

    try {
      const response = await api.content.actions.action.shareMultiNodeDownload(data)
      return { ...response.data, bgActionId }
    } catch (error) {
      commit(
        `background_actions/${[BackgroundMutations.UPDATE_BACKGROUND_ACTION_STATUS]}`,
        {bgActionId, status: 'error'},
        { root: true }
      )
      throw error
    }
  },

  [ContentActions.SET_CUSTOM_ICON]({ dispatch, commit }, {nodeId, formData}) {
    return new Promise((resolve, reject) => {
      api.content.actions.action
        .setCustomIcon(nodeId, formData)
        .then(() => {
          dispatch(`content/entry/${[EntryActions.GET_ENTRY_DATA]}`, nodeId, { root:true })
            .then((entry) => {
              commit(`content/entry/${[EntryMutations.UPDATE_ENTRY]}`, {nodeId, params: entry}, { root:true })

              resolve(entry)
            })
        })
        .catch((error) => reject(error))
    })
  }
}
